import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Landmark, ArrowDownLeft, ArrowUpRight, ArrowLeftRight } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, Tooltip } from "recharts";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("en-TZ").format(Math.round(n || 0));

export default function AccountStatement() {
  const { accountName } = useParams();
  const name = decodeURIComponent(accountName);
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [allAccounts, setAllAccounts] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [historicalCount, setHistoricalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [transferOpen, setTransferOpen] = useState(false);
  const [toAccount, setToAccount] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [transferReason, setTransferReason] = useState("");
  const [transferring, setTransferring] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/chart-of-accounts/", { params: { limit: 200 } }),
      api.get("/deposits/", { params: { limit: 1000 } }),
      api.get("/expenses/", { params: { limit: 2000 } }),
      api.get("/fund-transfers/", { params: { limit: 1000 } }),
    ]).then(([acc, dep, exp, xfer]) => {
      const accounts = acc.data.items || [];
      setAllAccounts(accounts);
      setAccount(accounts.find((a) => a.name === name) || null);
      setHistoricalCount(
        (exp.data.items || []).filter((e) => e.paid_by === name && e.status === "approved" && !e.approved_at).length
      );

      const deposits = (dep.data.items || [])
        .filter((d) => d.bank === name)
        .map((d) => ({
          id: `dep-${d.id}`, date: (d.date || d.created_at || "").slice(0, 10),
          // A payment correction posts a negative deposit when money comes back out.
          description: d.reference || "Deposit", direction: Number(d.amount) < 0 ? "out" : "in",
          amount: Math.abs(Number(d.amount) || 0),
        }));
      // Only expenses approved through the live workflow ever actually
      // debited this account's balance (approved_at is set by that flow) --
      // the historical bulk-imported expenses were inserted with a
      // pre-set "approved" status but never touched any account balance,
      // since the real opening balance already accounts for all of that
      // prior history. Including them here would double-count it and make
      // the running balance below disagree with the real Current Balance.
      const expenses = (exp.data.items || [])
        .filter((e) => e.paid_by === name && e.status === "approved" && e.approved_at)
        .map((e) => ({
          id: `exp-${e.id}`, date: (e.date || e.created_at || "").slice(0, 10),
          description: e.description || e.category || "Expense", direction: "out", amount: Number(e.amount) || 0,
        }));
      const transfersOut = (xfer.data.items || [])
        .filter((t) => t.from_account === name)
        .map((t) => ({
          id: `xout-${t.id}`, date: (t.date || t.created_at || "").slice(0, 10),
          description: `Transfer to ${t.to_account}${t.reason ? ` — ${t.reason}` : ""}`, direction: "out", amount: Number(t.amount) || 0,
        }));
      const transfersIn = (xfer.data.items || [])
        .filter((t) => t.to_account === name)
        .map((t) => ({
          id: `xin-${t.id}`, date: (t.date || t.created_at || "").slice(0, 10),
          description: `Transfer from ${t.from_account}${t.reason ? ` — ${t.reason}` : ""}`, direction: "in", amount: Number(t.amount) || 0,
        }));

      // Running balance only means something computed in chronological
      // order (oldest first) -- ties broken by direction so same-day
      // credits land before same-day debits, matching how money would
      // actually have been available to spend.
      const chronological = [...deposits, ...expenses, ...transfersOut, ...transfersIn].sort((a, b) => {
        const byDate = new Date(a.date) - new Date(b.date);
        if (byDate !== 0) return byDate;
        return a.direction === "in" ? -1 : 1;
      });
      let running = 0;
      const withRunning = chronological.map((row) => {
        running += row.direction === "in" ? row.amount : -row.amount;
        return { ...row, running };
      });
      setLedger(withRunning.reverse()); // newest first for display
    }).finally(() => setLoading(false));
  }, [name]);

  useEffect(() => { load(); }, [load]);

  const openTransfer = () => {
    setToAccount(""); setTransferAmount(""); setTransferReason("");
    setTransferOpen(true);
  };

  const submitTransfer = async () => {
    const amount = Number(transferAmount);
    if (!toAccount) { toast.error("Select a destination account"); return; }
    if (!amount || amount <= 0) { toast.error("Enter an amount greater than zero"); return; }
    if (!transferReason.trim()) { toast.error("A reason is required"); return; }
    setTransferring(true);
    try {
      await api.post("/fund-transfers/", {
        from_account: name, to_account: toAccount, amount, reason: transferReason.trim(),
      });
      toast.success(`Transferred TZS ${fmt(amount)} to ${toAccount}`);
      setTransferOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTransferring(false);
    }
  };

  const totalIn = ledger.filter((r) => r.direction === "in").reduce((s, r) => s + r.amount, 0);
  const totalOut = ledger.filter((r) => r.direction === "out").reduce((s, r) => s + r.amount, 0);
  const chartData = ledger.slice().reverse(); // chronological for the sparkline

  if (loading) return (
    <div className="space-y-6">
      <Skeleton className="h-24 rounded-2xl" />
      <Skeleton className="h-96 rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="account-statement-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-amber-500 font-semibold">Account Statement</p>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">{name}</h1>
          </div>
        </div>
        <Button onClick={openTransfer} data-testid="transfer-money-button" className="kn-gradient-bg text-white border-0 hover:opacity-95">
          <ArrowLeftRight className="w-4 h-4 mr-2" /> Transfer Money
        </Button>
      </div>

      <div className="kn-gradient-border relative p-7 rounded-2xl border border-border bg-card overflow-hidden">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-gradient-to-br from-blue-500/20 to-amber-500/20 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Landmark className="w-4 h-4 text-primary" />
              <p className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">Current Balance</p>
            </div>
            <p className="font-display text-4xl sm:text-5xl font-bold kn-gradient-text">TZS {fmt(account?.balance)}</p>
            {account?.notes && <p className="text-xs text-muted-foreground mt-2 max-w-md">{account.notes}</p>}
            <div className="flex flex-wrap gap-2 mt-4">
              <Badge variant="outline" className="text-xs gap-1.5 border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                <ArrowDownLeft className="w-3 h-3" /> Money In TZS {fmt(totalIn)}
              </Badge>
              <Badge variant="outline" className="text-xs gap-1.5 border-rose-500/30 text-rose-600 dark:text-rose-400">
                <ArrowUpRight className="w-3 h-3" /> Money Out TZS {fmt(totalOut)}
              </Badge>
            </div>
          </div>
          {chartData.length > 1 && (
            <div className="w-full lg:w-64 h-20 shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="gBalance" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Tooltip
                    formatter={(v) => [`TZS ${fmt(v)}`, "Balance"]}
                    labelFormatter={(_, p) => p?.[0]?.payload?.date}
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  />
                  <Area type="monotone" dataKey="running" stroke="hsl(var(--primary))" fill="url(#gBalance)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <Card className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="p-5 border-b border-border">
          <h3 className="font-display text-base font-semibold">Transaction History</h3>
          <p className="text-xs text-muted-foreground">Every deposit and approved expense through this account, most recent first</p>
          {historicalCount > 0 && (
            <p className="text-[11px] text-muted-foreground mt-2 bg-muted/40 rounded-md px-2.5 py-1.5 inline-block">
              {historicalCount} older expense{historicalCount === 1 ? "" : "s"} imported from your historical records
              {" "}aren't listed here — the Current Balance above already accounts for that prior spending, so including
              {" "}them again would double-count it. See Expenses for the full history.
            </p>
          )}
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ledger.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-12">No transactions recorded yet</TableCell></TableRow>
              ) : ledger.map((row) => (
                <TableRow key={row.id} data-testid={`ledger-row-${row.id}`}>
                  <TableCell className="text-xs whitespace-nowrap">{row.date || "—"}</TableCell>
                  <TableCell className="text-sm">{row.description}</TableCell>
                  <TableCell>
                    {row.direction === "in" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">
                        <ArrowDownLeft className="w-3 h-3" /> In
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-500/20">
                        <ArrowUpRight className="w-3 h-3" /> Out
                      </span>
                    )}
                  </TableCell>
                  <TableCell className={`text-right font-medium ${row.direction === "in" ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                    {row.direction === "in" ? "+" : "-"}{fmt(row.amount)}
                  </TableCell>
                  <TableCell className="text-right font-display font-semibold">{fmt(row.running)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Transfer Money</DialogTitle>
            <DialogDescription>
              From {name} — available TZS {fmt(account?.balance)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em]">
                To Account<span className="text-rose-500 ml-1">*</span>
              </Label>
              <SearchableSelect
                value={toAccount}
                onValueChange={setToAccount}
                options={allAccounts.filter((a) => a.name !== name).map((a) => ({ value: a.name, label: a.name }))}
                placeholder="Select destination account…"
                testId="transfer-to-account-select"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em]">
                Amount<span className="text-rose-500 ml-1">*</span>
              </Label>
              <MoneyInput
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                data-testid="transfer-amount-input"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em]">
                Reason<span className="text-rose-500 ml-1">*</span>
              </Label>
              <Input
                value={transferReason}
                onChange={(e) => setTransferReason(e.target.value)}
                data-testid="transfer-reason-input"
                placeholder="e.g. Top up petty cash for site visit"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTransferOpen(false)}>Cancel</Button>
            <Button onClick={submitTransfer} disabled={transferring} data-testid="submit-transfer-button" className="kn-gradient-bg text-white border-0">
              {transferring ? "Transferring…" : "Transfer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
